import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  ButtonGroup,
  Column,
  Dialog,
  FormField,
  PlusIcon,
  Row,
  Select,
  Text,
  Textarea,
  TextInput,
  TextWithTooltip,
} from "@hightouch/htui";
import { Controller, useWatch } from "react-hook-form";
import { z } from "zod";

import { SwitchFormField } from "src/components/switch-form-field";
import { Form, useHightouchForm } from "src/components/form";
import {
  countOf,
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";
import { useUser } from "src/contexts/user-context";
import {
  type AmpBrandProfileOptionsQuery,
  AmpProjectStatus,
  type AmpWorkflowStatusFragment,
  type AmpProjectRowFragment,
  useAmpBrandProfileOptionsQuery,
  useCreateAmpBoardProjectMutation,
} from "src/graphql";
import { useBoard } from "src/pages/projects/board-context";
import { useWorkflows, useWorkspaceMembers } from "src/pages/projects/hooks";
import {
  defaultBrandProfileId,
  PROJECT_STATUS_CATEGORY,
  PROJECT_STATUS_LABELS,
  PROJECT_STATUSES,
  type ProjectStatus,
  defaultWorkflowId,
  endOfDayIso,
  sortStatuses,
  startOfDayIso,
  toProjectStatus,
} from "src/pages/projects/utils";
import { Link, useNavigate } from "src/router";
import { noun } from "src/utils/noun";

import { ProjectIconPicker } from "./project-icon";
import { MemberMultiSelect, PeoplePicker } from "./people";
import { ProjectProgressBar } from "./project-progress";
import { type Appearance, PropertyPicker } from "./property-picker";
import { DateField, DatePickerButton } from "./task-properties";
import { CATEGORY_ICONS } from "./status-icon";
import { StatusMenu, type StatusMenuOption } from "./status-menu";

const PROJECT_STATUS_MENU_OPTIONS: StatusMenuOption[] = PROJECT_STATUSES.map(
  (value) => ({
    value,
    label: PROJECT_STATUS_LABELS[value],
    icon: CATEGORY_ICONS[PROJECT_STATUS_CATEGORY[value]],
  }),
);

type ProjectPickerProps = {
  project: Pick<AmpProjectRowFragment, "id" | "status">;
  appearance?: Appearance;
  /** Shows only the icon or avatar, for dense rows. */
  compact?: boolean;
};

export const ProjectStatusPicker = ({
  project,
  appearance = "pill",
  compact = false,
  valueDisplay,
}: Omit<ProjectPickerProps, "appearance"> & {
  appearance?: Appearance | "badge";
  /** What a "field" or "badge" shows in place of the status name, such as the status badge. */
  valueDisplay?: ReactNode;
}) => {
  const { projects } = useBoard();

  return (
    <StatusMenu
      options={PROJECT_STATUS_MENU_OPTIONS}
      value={toProjectStatus(project.status)}
      appearance={appearance}
      iconOnly={compact}
      valueDisplay={valueDisplay}
      onChange={(status) => projects.update(project.id, { status })}
    />
  );
};

type ProjectDatePickerProps = {
  project: Pick<AmpProjectRowFragment, "id" | "start_at" | "due_at" | "status">;
  appearance?: Appearance;
};

export const ProjectDueDatePicker = ({
  project,
  appearance = "pill",
  hideWhenEmpty = false,
}: ProjectDatePickerProps & {
  /** Hides the picker when no date is set, for dense rows. */
  hideWhenEmpty?: boolean;
}) => {
  const { projects } = useBoard();
  const isOverdue =
    Boolean(project.due_at) &&
    toProjectStatus(project.status) !== "done" &&
    new Date(project.due_at ?? "").getTime() < Date.now();

  if (hideWhenEmpty && !project.due_at) return null;

  return (
    <DatePickerButton
      value={project.due_at}
      appearance={appearance}
      isOverdue={isOverdue}
      // The calendar offers no due date before the start date.
      minDate={project.start_at ? new Date(project.start_at) : undefined}
      onChange={(date) =>
        projects.update(project.id, {
          due_at: date ? endOfDayIso(date) : null,
        })
      }
    />
  );
};

export const ProjectStartDatePicker = ({
  project,
  appearance = "pill",
}: ProjectDatePickerProps) => {
  const { projects } = useBoard();
  return (
    <DatePickerButton
      value={project.start_at}
      label="Start date"
      appearance={appearance}
      maxDate={project.due_at ? new Date(project.due_at) : undefined}
      onChange={(date) =>
        projects.update(project.id, {
          start_at: date ? startOfDayIso(date) : null,
        })
      }
    />
  );
};

const BRAND_PROFILES_PATH = "/context-hub/brands";

type BrandProfileOption = AmpBrandProfileOptionsQuery["brand_profiles"][number];

// Module-level so React Query's `select` stays referentially stable.
const selectBrandProfiles = (data: AmpBrandProfileOptionsQuery) =>
  data.brand_profiles;

/** The workspace's live brand profiles, default first, and the one a new project starts with. */
function useBrandProfileOptions() {
  const { data: brandProfiles = [], isLoading } =
    useAmpBrandProfileOptionsQuery(undefined, { select: selectBrandProfiles });
  return {
    brandProfiles,
    defaultId: defaultBrandProfileId(brandProfiles),
    isLoading,
  };
}

function brandProfileLabel(profile: BrandProfileOption) {
  return profile.is_default ? `${profile.name} (default)` : profile.name;
}

export const ProjectBrandProfilePicker = ({
  project,
  appearance = "pill",
}: {
  project: Pick<
    AmpProjectRowFragment,
    "id" | "brand_profile_id" | "brand_profile"
  >;
  appearance?: Appearance;
}) => {
  const { projects } = useBoard();
  const { brandProfiles, isLoading } = useBrandProfileOptions();
  const name =
    brandProfiles.find((profile) => profile.id === project.brand_profile_id)
      ?.name ?? project.brand_profile?.name;

  return (
    <PropertyPicker
      aria-label={`Brand profile: ${name ?? "none"}. Change brand profile`}
      appearance={appearance}
      isLoading={isLoading}
      options={brandProfiles}
      value={project.brand_profile_id ?? undefined}
      optionValue={(profile) => profile.id}
      optionLabel={brandProfileLabel}
      placeholder="Search brand profiles…"
      emptyOptionsMessage="No brand profiles yet"
      onChange={(brandProfileId) => {
        if (brandProfileId && brandProfileId !== project.brand_profile_id) {
          projects.update(project.id, { brand_profile_id: brandProfileId });
        }
      }}
    >
      <TextWithTooltip color={name ? undefined : "text.tertiary"}>
        {name ?? "No brand profile"}
      </TextWithTooltip>
    </PropertyPicker>
  );
};

type ProjectMembersProps = {
  project: Pick<AmpProjectRowFragment, "id" | "owners" | "members">;
  appearance?: Appearance;
  /** Shows only the avatars, for dense rows. */
  compact?: boolean;
};

/** Everyone on the project. Its owner rows show as members too, and stay owners while they're picked. */
export const ProjectMembersPicker = ({
  project,
  appearance = "pill",
  compact = false,
}: ProjectMembersProps) => {
  const { projects } = useBoard();
  const ownerIds = new Set(
    project.owners.map((owner) => String(owner.user_id)),
  );

  return (
    <PeoplePicker
      label="Members"
      emptyLabel="No members"
      people={[...project.owners, ...project.members]}
      appearance={appearance}
      avatarsOnly={compact}
      onChange={(userIds) =>
        projects.setPeople(project.id, {
          ownerIds: userIds.filter((id) => ownerIds.has(id)),
          memberIds: userIds.filter((id) => !ownerIds.has(id)),
        })
      }
    />
  );
};

/** A project's progress, due date, status and people, for its group header. */
export const ProjectGroupSummary = ({
  project,
}: {
  project: AmpProjectRowFragment;
}) => (
  <Row align="center" gap={1} flexShrink={0}>
    <Box width={20} mr={2}>
      <ProjectProgressBar project={project} />
    </Box>
    <ProjectDueDatePicker project={project} appearance="ghost" hideWhenEmpty />
    <ProjectStatusPicker project={project} appearance="ghost" compact />
    <ProjectMembersPicker project={project} appearance="ghost" compact />
  </Row>
);

/** The create form's schema. A brand profile is required only while the workspace has one to pick. */
function projectSchema(requiresBrandProfile: () => boolean) {
  return z
    .object({
      title: z.string().trim().min(1, "Name is required").max(200),
      description: z.string().max(5000),
      status: z.enum(PROJECT_STATUSES),
      trackTasks: z.boolean(),
      workflowId: z.string(),
      brandProfileId: z.string(),
      memberUserIds: z.array(z.string()),
      startAt: z.string(),
      dueAt: z.string(),
      icon: z.string().nullable(),
    })
    .superRefine((values, context) => {
      if (
        values.startAt &&
        values.dueAt &&
        new Date(values.startAt) > new Date(values.dueAt)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dueAt"],
          message: "Due date can't be before the start date",
        });
      }
      if (requiresBrandProfile() && !values.brandProfileId) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["brandProfileId"],
          message: "Brand profile is required",
        });
      }
    });
}

type ProjectFormState = z.infer<ReturnType<typeof projectSchema>>;

const STATUS_NOUN = noun("status", "statuses");

const PROJECT_STATUS_TO_ENUM: Record<ProjectStatus, AmpProjectStatus> = {
  draft: AmpProjectStatus.Draft,
  in_progress: AmpProjectStatus.InProgress,
  done: AmpProjectStatus.Done,
};

type CreateProjectDialogProps = {
  onClose: () => void;
  /** Called with the new project instead of opening it, so the caller keeps the user where they are. */
  onCreated?: (projectId: string) => void;
};

export const CreateProjectDialog = ({
  onClose,
  onCreated,
}: CreateProjectDialogProps) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { byId: memberById, isLoading: isLoadingMembers } =
    useWorkspaceMembers();
  const currentUserId = user ? String(user.id) : undefined;
  const { workflows } = useWorkflows();
  const brandProfiles = useBrandProfileOptions();
  const hasBrandProfiles = brandProfiles.brandProfiles.length > 0;
  // The resolver is built once and the brand profiles load after it, so it reads them through a ref when it runs.
  const requiresBrandProfileRef = useRef(true);
  requiresBrandProfileRef.current = brandProfiles.isLoading || hasBrandProfiles;
  const resolver = useMemo(
    () => zodResolver(projectSchema(() => requiresBrandProfileRef.current)),
    [],
  );
  const createProject = useCreateAmpBoardProjectMutation();

  const form = useHightouchForm<ProjectFormState>({
    resolver,
    defaultValues: {
      title: "",
      description: "",
      status: "draft",
      trackTasks: true,
      workflowId: "",
      brandProfileId: "",
      memberUserIds:
        currentUserId && memberById.has(currentUserId) ? [currentUserId] : [],
      startAt: "",
      dueAt: "",
      icon: null,
    },
    success: `${TERMS.project.Singular} created`,
    onSubmit: async (values) => {
      const result = await createProject.mutateAsync({
        input: {
          title: values.title.trim(),
          description: values.description.trim() || null,
          status: PROJECT_STATUS_TO_ENUM[values.status],
          // Without a chosen workflow, a task board starts on the workspace default.
          taskBoard: values.trackTasks,
          workflowId: values.trackTasks ? values.workflowId || null : null,
          brandProfileId: values.brandProfileId || null,
          // The creator owns what they join; the server keeps owners out of the members list.
          ownerUserIds:
            currentUserId && values.memberUserIds.includes(currentUserId)
              ? [currentUserId]
              : [],
          memberUserIds: values.memberUserIds,
          startAt: values.startAt || null,
          dueAt: values.dueAt || null,
          icon: values.icon,
        },
      });
      onClose();
      const projectId = result.createAmpProject.id;
      if (onCreated) onCreated(projectId);
      else navigate(PROJECTS_PATHS.project(projectId));
    },
  });
  const [startAt, dueAt, trackTasks] = useWatch({
    control: form.control,
    name: ["startAt", "dueAt", "trackTasks"],
  });

  const { setValue, getValues, resetField } = form;
  const isMembersDirty = Boolean(form.formState.dirtyFields.memberUserIds);
  useEffect(() => {
    if (isLoadingMembers || !currentUserId || isMembersDirty) return;
    // Admins viewing another workspace may not be members and cannot join its projects.
    if (memberById.has(currentUserId)) {
      resetField("memberUserIds", { defaultValue: [currentUserId] });
    }
  }, [isLoadingMembers, currentUserId, memberById, isMembersDirty, resetField]);

  // The brand profiles load after the form mounts, so the default lands once they do, unless one was already picked.
  useEffect(() => {
    if (brandProfiles.defaultId && !getValues("brandProfileId")) {
      setValue("brandProfileId", brandProfiles.defaultId);
    }
  }, [brandProfiles.defaultId, getValues, setValue]);

  return (
    <Form form={form}>
      <Dialog
        isOpen
        variant="form"
        title={`Create ${TERMS.project.singular}`}
        width="lg"
        onClose={onClose}
        actions={
          <ButtonGroup>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              isLoading={form.formState.isSubmitting}
              onClick={() => form.submit()}
            >
              Create {TERMS.project.singular}
            </Button>
          </ButtonGroup>
        }
      >
        <Column gap={4}>
          <Controller
            control={form.control}
            name="title"
            render={({ field, fieldState }) => (
              <FormField
                label={`${TERMS.project.Singular} name`}
                error={fieldState.error?.message}
              >
                <Row align="center" gap={1}>
                  <Controller
                    control={form.control}
                    name="icon"
                    render={({ field: iconField }) => (
                      <ProjectIconPicker
                        icon={iconField.value}
                        size={32}
                        onChange={iconField.onChange}
                      />
                    )}
                  />
                  <TextInput
                    autoFocus
                    width="100%"
                    placeholder="Spring launch"
                    value={field.value}
                    onChange={field.onChange}
                  />
                </Row>
              </FormField>
            )}
          />
          <Controller
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormField label="Description" isOptional>
                <Textarea
                  width="100%"
                  placeholder="Plan the spring launch campaign"
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormField>
            )}
          />
          <Row gap={4}>
            <DateField
              name="startAt"
              label="Start date"
              dayEdge="start"
              maxDate={dueAt ? new Date(dueAt) : undefined}
            />
            <DateField
              name="dueAt"
              label="Due date"
              dayEdge="end"
              minDate={startAt ? new Date(startAt) : undefined}
            />
          </Row>
          <MemberMultiSelect
            name="memberUserIds"
            label="Members"
            emptyLabel="Select members"
          />
          <Controller
            control={form.control}
            name="brandProfileId"
            render={({ field, fieldState }) => (
              <FormField
                label="Brand profile"
                isOptional={!brandProfiles.isLoading && !hasBrandProfiles}
                description={`The brand this ${TERMS.project.singular}'s work follows.`}
                error={fieldState.error?.message}
              >
                {!brandProfiles.isLoading && !hasBrandProfiles ? (
                  <Text color="text.secondary">
                    No brand profiles yet.{" "}
                    <Link href={BRAND_PROFILES_PATH}>Create one</Link> in
                    Context Hub to set it here.
                  </Text>
                ) : (
                  <Select
                    width="100%"
                    isLoading={brandProfiles.isLoading}
                    options={brandProfiles.brandProfiles}
                    optionValue={(profile) => profile.id}
                    optionLabel={brandProfileLabel}
                    placeholder="Choose a brand profile"
                    value={field.value || undefined}
                    onChange={(value) => field.onChange(value ?? "")}
                  />
                )}
              </FormField>
            )}
          />
          <Controller
            control={form.control}
            name="trackTasks"
            render={({ field }) => (
              <SwitchFormField
                size="md"
                label={`Track ${TERMS.task.plural}`}
                description={`Give this ${TERMS.project.singular} a ${TERMS.task.singular} board. You can add one later.`}
                isChecked={field.value}
                onChange={(isChecked: boolean) => {
                  field.onChange(isChecked);
                  if (isChecked && !form.getValues("workflowId")) {
                    form.setValue("workflowId", defaultWorkflowId(workflows));
                  }
                }}
              />
            )}
          />
          {trackTasks && (
            <Controller
              control={form.control}
              name="workflowId"
              render={({ field }) => (
                <FormField
                  label={TERMS.workflow.Singular}
                  description={`Choose the statuses this ${TERMS.project.singular}'s ${TERMS.task.plural} move through. You can't change the ${TERMS.workflow.singular} after creating the ${TERMS.project.singular}.`}
                >
                  <WorkflowSelect
                    workflows={workflows}
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormField>
              )}
            />
          )}
        </Column>
      </Dialog>
    </Form>
  );
};

type WorkflowOption = {
  id: string;
  name: string;
  is_default: boolean;
  statuses: Pick<AmpWorkflowStatusFragment, "name" | "category" | "position">[];
};

/** Picks one of the workspace's workflows, its default marked and its statuses listed in order. Empty stands for the default, so the field shows it. */
export const WorkflowSelect = ({
  workflows,
  value,
  onChange,
}: {
  workflows: WorkflowOption[];
  value: string;
  onChange: (workflowId: string) => void;
}) => (
  <Select
    width="100%"
    options={workflows}
    optionValue={(workflow) => workflow.id}
    optionLabel={(workflow) =>
      `${workflow.name} · ${countOf(workflow.statuses.length, STATUS_NOUN)}`
    }
    optionDescription={(workflow) =>
      sortStatuses(workflow.statuses)
        .map((status) => status.name)
        .join(", ")
    }
    optionAccessories={(workflow) =>
      workflow.is_default
        ? {
            rightAccessory: {
              type: "badge",
              text: "Default",
              variant: "info",
            },
          }
        : undefined
    }
    value={value || defaultWorkflowId(workflows) || undefined}
    onChange={(next) => onChange(next ?? "")}
  />
);

/** Opens the new-project dialog from a button. */
export const NewProjectButton = ({
  variant = "primary",
  size,
}: {
  variant?: "primary" | "secondary";
  size?: "sm" | "md";
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Button
        variant={variant}
        size={size}
        shape="circle"
        icon={PlusIcon}
        onClick={() => setIsOpen(true)}
      >
        Create {TERMS.project.singular}
      </Button>
      {isOpen && <CreateProjectDialog onClose={() => setIsOpen(false)} />}
    </>
  );
};
